<template>
  <div
    class="receipt-stage mx-auto w-full max-w-sm"
    :class="{ 'is-printing': printing }"
    dir="rtl"
  >
    <div class="relative z-20 rounded-2xl border border-default bg-default p-5 shadow-lg">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 text-start">
          <h3 class="text-base font-bold sm:text-lg">
            {{ heading }}
          </h3>
          <p
            v-if="subtitle"
            class="mt-1 text-xs text-muted sm:text-sm"
          >
            {{ subtitle }}
          </p>
        </div>
        <p
          v-if="amountLabel"
          class="shrink-0 text-end"
        >
          <span class="block text-[11px] text-muted">مبلغ</span>
          <span class="text-sm font-bold tabular-nums">{{ amountLabel }}</span>
        </p>
      </div>

      <div class="relative mt-4 min-h-5">
        <div class="status-processing absolute inset-x-0 top-0 flex items-center gap-2 text-xs text-muted">
          <span class="ticket-spinner size-3.5 rounded-full border-2 border-muted/30 border-t-muted" />
          در حال صدور تیکت
        </div>
        <div class="status-done absolute inset-x-0 top-0 flex items-center gap-2 text-xs text-emerald-600">
          <UIcon
            name="i-lucide-check"
            class="size-3.5"
          />
          نوبت ثبت شد
        </div>
      </div>
    </div>

    <div class="paper-wrap relative z-10 mx-4">
      <div class="paper-inner">
        <div class="paper-sheet -mt-2 px-5 pb-3 pt-6">
          <div class="text-center">
            <p
              v-if="ticket.siteTitle"
              class="text-[11px] font-semibold tracking-wide text-(--ticket-muted)"
            >
              {{ ticket.siteTitle }}
            </p>
            <p class="mt-1 text-xs font-bold tracking-widest text-(--ticket-ink)">
              تیکت نوبت
            </p>
            <p class="mt-2 text-lg font-bold tabular-nums text-primary">
              {{ formatAppointmentCodeFa(ticket.code) }}
            </p>
            <p class="mt-0.5 text-[11px] text-(--ticket-muted)">
              شماره پیگیری
            </p>
          </div>

          <div class="my-3 border-t border-dashed border-(--ticket-line)" />

          <dl class="space-y-1.5 text-xs">
            <div
              v-for="row in rows"
              :key="row.label"
              class="flex items-start justify-between gap-3"
            >
              <dt class="shrink-0 text-(--ticket-muted)">
                {{ row.label }}
              </dt>
              <dd class="min-w-0 text-end font-medium wrap-break-word text-(--ticket-ink) tabular-nums">
                {{ row.value }}
              </dd>
            </div>
          </dl>

          <p class="mt-4 text-center text-[11px] text-(--ticket-muted)">
            این تیکت را هنگام مراجعه همراه داشته باشید
          </p>
        </div>
        <div class="paper-edge mx-px" />
      </div>
    </div>

    <div class="mt-4 space-y-2">
      <div class="flex gap-2">
        <UButton
          class="flex-1 justify-center"
          icon="i-lucide-download"
          :loading="generating"
          @click="downloadReceipt"
        >
          دانلود رسید
        </UButton>
        <UButton
          to="/patient"
          variant="soft"
          color="neutral"
          class="shrink-0"
          icon="i-lucide-folder-heart"
        >
          سوابق من
        </UButton>
        <UButton
          variant="soft"
          color="neutral"
          class="shrink-0"
          :icon="copied ? 'i-lucide-check' : 'i-lucide-copy'"
          @click="copyCode"
        >
          {{ copied ? 'کپی شد' : 'کپی شماره' }}
        </UButton>
      </div>
      <p
        v-if="downloadError"
        class="text-center text-xs text-red-600"
      >
        {{ downloadError }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BookingTicket } from '~/types/booking'
import { faDate } from '~/utils/faDate'
import {
  formatAppointmentCode,
  formatAppointmentCodeFa,
  formatRialFa,
  paymentStatusLabel,
  visitTypeLabel,
} from '~/utils/booking/ticketCode'
import { useTicketImage } from '~/composables/useTicketImage'

const props = defineProps<{
  ticket: BookingTicket
  title?: string
  description?: string
}>()

const toast = useToast()
const { generating, error: downloadError, download } = useTicketImage()
const printing = ref(false)
const copied = ref(false)
let copiedTimer: ReturnType<typeof setTimeout> | null = null

const heading = computed(() => props.title || 'نوبت شما ثبت شد')
const subtitle = computed(() => props.description || 'پیامک تأیید به شماره شما ارسال می‌شود.')

const rows = computed(() => {
  const items: { label: string; value: string }[] = [
    { label: 'بیمار', value: props.ticket.patientName },
    { label: 'خدمت', value: props.ticket.serviceTitle },
  ]
  if (props.ticket.doctorName) {
    items.push({ label: 'پزشک', value: props.ticket.doctorName })
  }
  items.push(
    { label: 'نوع نوبت', value: visitTypeLabel(props.ticket.visitType) },
    { label: 'زمان', value: faDate(props.ticket.startAt, 'long') },
    { label: 'پرداخت', value: paymentStatusLabel(props.ticket.paymentStatus, props.ticket.amountDue) },
  )
  const amount = props.ticket.paidAmount > 0 ? props.ticket.paidAmount : props.ticket.amountDue
  if (amount > 0) {
    items.push({ label: 'مبلغ', value: formatRialFa(amount) })
  }
  return items
})

const amountLabel = computed(() => {
  const amount = props.ticket.paidAmount > 0 ? props.ticket.paidAmount : props.ticket.amountDue
  if (amount <= 0) return ''
  return formatRialFa(amount)
})

function play() {
  printing.value = false
  nextTick(() => {
    window.setTimeout(() => {
      printing.value = true
    }, 120)
  })
}

async function downloadReceipt() {
  await download(props.ticket)
  if (downloadError.value) {
    toast.add({ title: downloadError.value, color: 'error' })
    return
  }
  toast.add({ title: 'رسید ذخیره شد', color: 'success' })
}

async function copyCode() {
  try {
    await navigator.clipboard.writeText(formatAppointmentCode(props.ticket.code))
    copied.value = true
    toast.add({ title: 'شمارهٔ پیگیری کپی شد', color: 'success' })
    if (copiedTimer) clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => {
      copied.value = false
    }, 2000)
  }
  catch {
    toast.add({ title: 'کپی نشد. شماره را دستی بردارید.', color: 'error' })
  }
}

onMounted(play)
watch(() => props.ticket.id, play)
onBeforeUnmount(() => {
  if (copiedTimer) clearTimeout(copiedTimer)
})
</script>

<style scoped>
.receipt-stage {
  --ticket-paper: oklch(0.985 0.004 95);
  --ticket-ink: oklch(0.30 0.006 260);
  --ticket-muted: oklch(0.50 0.006 260);
  --ticket-line: oklch(0.88 0.004 95);
}

.paper-wrap {
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  will-change: max-height, opacity;
}

.paper-inner {
  min-height: 0;
}

.paper-sheet {
  background: var(--ticket-paper);
  color: var(--ticket-ink);
  box-shadow: 0 16px 30px -10px oklch(0 0 0 / 0.28);
  transform: translate3d(0, -16px, 0);
  opacity: 0.85;
}

.paper-edge {
  height: 10px;
  background:
    radial-gradient(circle at 6px 12px, transparent 6px, var(--ticket-paper) 6.5px)
    0 0 / 12px 10px repeat-x;
  filter: drop-shadow(0 2px 3px oklch(0 0 0 / 0.12));
}

@keyframes ticketReveal {
  0% {
    max-height: 0;
    opacity: 0;
  }
  25% {
    opacity: 1;
  }
  100% {
    max-height: 560px;
    opacity: 1;
  }
}

@keyframes ticketFeed {
  from {
    transform: translate3d(0, -16px, 0);
    opacity: 0.85;
  }
  to {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
}

@keyframes ticket-spin {
  to { transform: rotate(360deg); }
}

.is-printing .paper-wrap {
  animation: ticketReveal 900ms cubic-bezier(0.22, 1, 0.36, 1) 280ms forwards;
}

.is-printing .paper-sheet {
  animation: ticketFeed 850ms cubic-bezier(0.33, 1, 0.38, 1) 320ms forwards;
}

.status-processing,
.status-done {
  transition: opacity .3s ease;
}

.status-processing { opacity: 1; }
.status-done { opacity: 0; }

.is-printing .status-processing { opacity: 0; }
.is-printing .status-done {
  opacity: 1;
  transition: opacity .3s ease .3s;
}

.ticket-spinner {
  display: inline-block;
  animation: ticket-spin 900ms linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .is-printing .paper-wrap {
    animation: none;
    max-height: 560px;
    opacity: 1;
  }
  .is-printing .paper-sheet {
    animation: none;
    transform: none;
    opacity: 1;
  }
  .ticket-spinner {
    animation: none;
  }
}
</style>
